import React from "react";
import { Box, FormHelperText } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import PhoneInput, { Country } from "react-phone-number-input";
import "react-phone-number-input/style.css";

// Restyles react-phone-number-input to match MUI's outlined TextField look
// (same border, radius, height and focus/error treatment as every other
// field in these forms) since the library doesn't accept an MUI `sx` prop
// directly and its own `inputStyle`/`error`/`helperText` props don't work
// the way MUI's do (see PhoneNumberField below for why).
//
// The border/background/focus ring live on the `.PhoneInput` row itself
// (not just the `<input>`) so the country-flag selector reads as part of
// the same field instead of floating unstyled to its left.
const StyledPhoneInput = styled(PhoneInput)(({ theme }) => {
  const borderColor =
    theme.palette.mode === "light"
      ? "rgba(0, 0, 0, 0.23)"
      : "rgba(255, 255, 255, 0.23)";

  return {
    "--PhoneInput-color--focus": theme.palette.primary.main,

    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "56px",
    padding: "0 14px",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${borderColor}`,
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(["border-color", "box-shadow"], {
      duration: theme.transitions.duration.shorter,
    }),

    "&:hover": {
      borderColor: theme.palette.text.primary,
    },
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },

    "& .PhoneInputCountry": {
      marginRight: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      borderRight: `1px solid ${borderColor}`,
      alignSelf: "stretch",
    },
    "& .PhoneInputCountryIcon": {
      width: "1.5em",
    },
    "& .PhoneInputCountrySelect": {
      cursor: "pointer",
    },

    "& .PhoneInputInput": {
      flex: 1,
      minWidth: 0,
      height: "100%",
      padding: 0,
      border: "none",
      outline: "none",
      backgroundColor: "transparent",
      color: theme.palette.text.primary,
      fontSize: "16px",
      fontFamily: theme.typography.fontFamily,
      "&::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },

    "&.PhoneInput--error": {
      borderColor: theme.palette.error.main,
      "& .PhoneInputCountry": {
        borderRightColor: theme.palette.error.main,
      },
    },
    "&.PhoneInput--error:focus-within": {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.error.main, 0.2)}`,
    },
  };
});

interface PhoneNumberFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  defaultCountry?: Country;
  required?: boolean;
  /** Shown as a red helper line and switches the field to its error style. */
  error?: string;
  /** Shown as the (non-error) helper line when `error` isn't set. */
  helperText?: string;
}

// Drop-in replacement for the raw `PhoneInput` used across the Firm/User/
// Client forms. `react-phone-number-input` renders as a plain `<input>`, so
// its `error`/`helperText` props aren't part of its API and are silently
// dropped — this wrapper renders a real MUI `FormHelperText` instead so
// validation feedback actually shows up, matching the `TextField`s next to it.
export default function PhoneNumberField({
  value,
  onChange,
  placeholder = "(555) 123-4567",
  defaultCountry = "US",
  required,
  error,
  helperText,
}: PhoneNumberFieldProps) {
  return (
    <Box sx={{ width: "100%" }}>
      <StyledPhoneInput
        country={defaultCountry}
        defaultCountry={defaultCountry}
        value={value}
        onChange={(val) => onChange(val || "")}
        placeholder={placeholder}
        className={error ? "PhoneInput--error" : undefined}
        required={required}
      />
      {(error || helperText) && (
        <FormHelperText error={!!error} sx={{ ml: 1.75 }}>
          {error || helperText}
        </FormHelperText>
      )}
    </Box>
  );
}
